/**
 * Patch tool: wraps the design in an embroidered patch.
 *
 * Styles
 *  - embroidered: background fill → design → satin border (fully stitched patch, cut out
 *    after sewing; the satin border seals the edge).
 *  - applique: placement line → (stop: place twill) → tack-down zigzag → (stop: trim fabric)
 *    → design → satin border. Stops are colour changes, which every format and machine supports.
 *
 * The outer edge of the satin border is the patch edge. The background reaches the border's
 * centre line so the border overlaps it (no gap). Patch objects carry `patch` roles so the patch
 * can be regenerated or removed without touching the design.
 */
import { t } from '../i18n/i18n';
import type { PathNode } from './geometry/bezier';
import { flattenPath } from './geometry/bezier';
import { offsetPolygons, offsetPolyline, unionPolygons } from './geometry/clip';
import { type PolygonWithHoles, polygonArea } from './geometry/polygon';
import { simplifyRing } from './geometry/simplify';
import type { Vec } from './geometry/vec';
import { createObject, createThread, ellipseNodes } from './model/design';
import { objectBBox, resolveGeometry } from './model/geometry';
import { defaultFill, defaultRunning, defaultSatin } from './model/params';
import type { Design, EmbObject, PatchRole, ThreadColor } from './model/types';
import { bboxUnion, emptyBBox, type BBox } from './geometry/polygon';

export type PatchShape = 'circle' | 'oval' | 'rect' | 'rounded' | 'shield' | 'contour';
export type PatchStyle = 'embroidered' | 'applique';

export interface PatchOptions {
  shape: PatchShape;
  style: PatchStyle;
  /** Outer size of the patch (ignored for 'contour'). */
  widthMm: number;
  heightMm: number;
  /** Patch centre in design coordinates (ignored for 'contour'). */
  center: Vec;
  /** Width of the satin border, mm. */
  borderWidth: number;
  /** Corner radius for 'rounded', mm. */
  cornerRadius: number;
  /** Distance from the design to the inner edge of the border for 'contour', mm. */
  contourMargin: number;
  backgroundColor: string;
  borderColor: string;
}

export const PATCH_LIMITS = {
  size: { min: 15, max: 400, step: 1, unit: 'mm' },
  borderWidth: { min: 1.5, max: 10, step: 0.5, unit: 'mm' },
  cornerRadius: { min: 0, max: 100, step: 1, unit: 'mm' },
  contourMargin: { min: 0.5, max: 30, step: 0.5, unit: 'mm' },
} as const;

export const PATCH_SHAPE_NAMES: Record<PatchShape, string> = {
  circle: 'Circle',
  oval: 'Oval',
  rect: 'Rectangle',
  rounded: 'Rounded rectangle',
  shield: 'Shield',
  contour: 'Follow the design',
};

const isPatch = (o: EmbObject) => !!o.patch;
/** Design objects that are actually sewn (hidden objects are not, so the patch ignores them). */
const isSewnDesign = (o: EmbObject) => !o.patch && o.visible;

/** Bounding box of the sewn design (patch objects and hidden objects excluded). */
export function designOnlyBBox(d: Design): BBox | null {
  const b = d.objects.filter(isSewnDesign).reduce((acc, o) => bboxUnion(acc, objectBBox(o)), emptyBBox());
  return b.maxX > b.minX ? b : null;
}

export function defaultPatchOptions(d: Design): PatchOptions {
  const b = designOnlyBBox(d) ?? { minX: 0, minY: 0, maxX: 60, maxY: 60 };
  const w = b.maxX - b.minX, h = b.maxY - b.minY;
  const border = 4;
  const margin = 4;
  const existingBorder = d.objects.find((o) => o.patch === 'border');
  const existingBg = d.objects.find((o) => o.patch === 'background');
  const hexOf = (o: EmbObject | undefined) => d.palette.find((c) => c.id === o?.colorId)?.hex;
  // a circle that encloses the design's bounding box with margin
  const diameter = Math.hypot(w, h) + 2 * (margin + border);
  return {
    shape: 'circle',
    style: 'embroidered',
    widthMm: Math.round(diameter),
    heightMm: Math.round(diameter),
    center: { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 },
    borderWidth: border,
    cornerRadius: 8,
    contourMargin: 3,
    backgroundColor: hexOf(existingBg) ?? '#f4f1ea',
    borderColor: hexOf(existingBorder) ?? '#1a1a1a',
  };
}

/** Clamps options to their limits. */
export function clampPatchOptions(o: PatchOptions): PatchOptions {
  const c = (v: number, l: { min: number; max: number }) => Math.min(l.max, Math.max(l.min, Number.isFinite(v) ? v : l.min));
  return {
    ...o,
    widthMm: c(o.widthMm, PATCH_LIMITS.size),
    heightMm: c(o.heightMm, PATCH_LIMITS.size),
    borderWidth: c(o.borderWidth, PATCH_LIMITS.borderWidth),
    cornerRadius: c(o.cornerRadius, PATCH_LIMITS.cornerRadius),
    contourMargin: c(o.contourMargin, PATCH_LIMITS.contourMargin),
  };
}

const K = 0.5522847498;

function roundedRectNodes(x: number, y: number, w: number, h: number, r: number): PathNode[] {
  r = Math.max(0, Math.min(r, w / 2, h / 2));
  if (r < 0.01) return [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
  const k = r * K;
  return [
    { x: x + r, y, in: { x: x + r - k, y } },
    { x: x + w - r, y, out: { x: x + w - r + k, y } },
    { x: x + w, y: y + r, in: { x: x + w, y: y + r - k } },
    { x: x + w, y: y + h - r, out: { x: x + w, y: y + h - r + k } },
    { x: x + w - r, y: y + h, in: { x: x + w - r + k, y: y + h } },
    { x: x + r, y: y + h, out: { x: x + r - k, y: y + h } },
    { x, y: y + h - r, in: { x, y: y + h - r + k } },
    { x, y: y + r, out: { x, y: y + r - k } },
  ];
}

function shieldNodes(x: number, y: number, w: number, h: number): PathNode[] {
  const r = Math.min(w, h) * 0.08;
  return [
    { x: x + r, y, in: { x: x + r * (1 - K), y } },
    { x: x + w - r, y, out: { x: x + w - r * (1 - K), y } },
    { x: x + w, y: y + r, in: { x: x + w, y: y + r * (1 - K) } },
    { x: x + w, y: y + h * 0.5, out: { x: x + w, y: y + h * 0.78 } },
    { x: x + w / 2, y: y + h, in: { x: x + w * 0.78, y: y + h * 0.9 }, out: { x: x + w * 0.22, y: y + h * 0.9 } },
    { x, y: y + h * 0.5, in: { x, y: y + h * 0.78 } },
    { x, y: y + r, out: { x, y: y + r * (1 - K) } },
  ];
}

/** Monotone chain convex hull. */
function convexHull(points: Vec[]): Vec[] {
  const p = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  if (p.length < 3) return p;
  const cross = (o: Vec, a: Vec, b: Vec) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Vec[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: Vec[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** Footprint of the design objects (filled areas, lines widened) as polygons. */
function designFootprint(d: Design): PolygonWithHoles[] {
  const polys: PolygonWithHoles[] = [];
  for (const o of d.objects) {
    if (!isSewnDesign(o)) continue;
    const g = resolveGeometry(o).geometry;
    if (g.kind === 'area') polys.push(...g.polygons);
    else if (g.kind === 'line') {
      const half = o.params.type === 'satin' ? o.params.lineWidth / 2 : 0.5;
      polys.push(...offsetPolyline(g.closed ? [...g.points, g.points[0]] : g.points, half));
    } else polys.push({ outer: [...g.railA, ...g.railB.slice().reverse()], holes: [] });
  }
  return polys.length ? unionPolygons(polys) : [];
}

/** Outer edge of the patch as a closed node path (design coordinates). */
export function patchOutline(d: Design, opts: PatchOptions): PathNode[] {
  const o = clampPatchOptions(opts);
  const { widthMm: w, heightMm: h, center: c } = o;
  const x = c.x - w / 2, y = c.y - h / 2;
  switch (o.shape) {
    case 'circle': {
      const r = Math.min(w, h) / 2;
      return ellipseNodes(c.x, c.y, r, r);
    }
    case 'oval':
      return ellipseNodes(c.x, c.y, w / 2, h / 2);
    case 'rect':
      return roundedRectNodes(x, y, w, h, 0);
    case 'rounded':
      return roundedRectNodes(x, y, w, h, o.cornerRadius);
    case 'shield':
      return shieldNodes(x, y, w, h);
    case 'contour': {
      const foot = designFootprint(d);
      if (!foot.length) return ellipseNodes(c.x, c.y, w / 2, h / 2);
      const dist = o.contourMargin + o.borderWidth;
      let grown = offsetPolygons(foot, dist, 'round');
      // merge separate islands by growing further; fall back to the convex hull
      for (let extra = 1; grown.length > 1 && extra <= 4; extra++) {
        grown = offsetPolygons(offsetPolygons(foot, dist + extra * dist, 'round'), -extra * dist, 'round');
      }
      let ring: Vec[];
      if (grown.length === 1) ring = grown[0].outer;
      else ring = convexHull(offsetPolygons(foot, dist, 'round').flatMap((p) => p.outer));
      return simplifyRing(ring, 0.05).map((p) => ({ x: p.x, y: p.y }));
    }
  }
}

/** Ring inset from the patch edge by `d` mm (flattened). */
function insetRing(outline: PathNode[], d: number): Vec[] {
  const flat = flattenPath(outline, true, 0.02);
  const res = offsetPolygons([{ outer: flat, holes: [] }], -d, 'round');
  if (!res.length) return flat;
  const largest = res.reduce((a, b) => (polygonArea(b) > polygonArea(a) ? b : a));
  return simplifyRing(largest.outer, 0.01);
}

/** Removes patch objects, and palette entries only they used. */
export function removePatch(d: Design): Design {
  const patchObjs = d.objects.filter(isPatch);
  if (!patchObjs.length) return d;
  const objects = d.objects.filter((o) => !isPatch(o));
  const used = new Set(objects.map((o) => o.colorId));
  const patchColors = new Set(patchObjs.map((o) => o.colorId));
  return { ...d, objects, palette: d.palette.filter((c) => used.has(c.id) || !patchColors.has(c.id)) };
}

const withRole = (o: EmbObject, role: PatchRole): EmbObject => ({ ...o, patch: role });

/** Adds (or replaces) the patch around the design. Patch parts the user had hidden stay hidden. */
export function applyPatch(design: Design, options: PatchOptions): Design {
  const o = clampPatchOptions(options);
  const hiddenRoles = new Set(design.objects.filter((x) => x.patch && !x.visible).map((x) => x.patch));
  const base = removePatch(design);
  const outline = patchOutline(base, o);
  const center = insetRing(outline, o.borderWidth / 2);
  const centerNodes: PathNode[] = center.map((p) => ({ x: p.x, y: p.y }));
  const palette: ThreadColor[] = [...base.palette];
  const thread = (hex: string, name: string) => {
    const t = createThread(hex, name);
    palette.push(t);
    return t;
  };
  const before: EmbObject[] = [];
  if (o.style === 'embroidered') {
    const bg = thread(o.backgroundColor, t('Patch – background'));
    const fill = { ...defaultFill(), angle: 135, spacing: 0.45 };
    before.push(withRole(createObject({ kind: 'area', outer: centerNodes, holes: [] }, fill, bg.id, t('Patch – background')), 'background'));
  } else {
    // Placement line shows where to lay the fabric; the colour change after it stops the machine.
    const place = thread(o.borderColor, t('Patch – placement (stop)'));
    before.push(withRole(createObject({ kind: 'line', nodes: centerNodes, closed: true }, { ...defaultRunning(), stitchLength: 3 }, place.id, t('Patch – placement line')), 'placement'));
    // Tack-down zigzag holds the fabric; another colour change lets the operator trim the fabric.
    const tack = thread(o.borderColor, t('Patch – tack-down (stop)'));
    const zig = { ...defaultSatin(), lineWidth: Math.max(1, o.borderWidth * 0.4), spacing: 2, pullCompensation: 0, underlay: { ...defaultSatin().underlay, centerRun: false } };
    before.push(withRole(createObject({ kind: 'line', nodes: centerNodes, closed: true }, zig, tack.id, t('Patch – tack-down')), 'tackdown'));
  }
  const border = thread(o.borderColor, t('Patch – border'));
  const borderParams = {
    ...defaultSatin(),
    lineWidth: o.borderWidth,
    spacing: 0.4,
    pullCompensation: 0.3,
    underlay: { ...defaultSatin().underlay, centerRun: false, edgeRun: true, zigzag: o.borderWidth >= 3 },
  };
  const borderObj = withRole(createObject({ kind: 'line', nodes: centerNodes, closed: true }, borderParams, border.id, t('Patch – satin border')), 'border');
  const keepHidden = (x: EmbObject): EmbObject => (x.patch && hiddenRoles.has(x.patch) ? { ...x, visible: false } : x);
  return {
    ...base,
    palette,
    objects: [...before.map(keepHidden), ...base.objects, keepHidden(borderObj)],
  };
}

export const hasPatch = (d: Design): boolean => d.objects.some(isPatch);
